import React, { useState, useEffect, useRef } from 'react';
import {
  Card, Typography, Button, Space, Radio, Input, Tag, Image, message,
  Spin, Tooltip, Dropdown, Row, Col, Divider, Empty, Select
} from 'antd';
import {
  ThunderboltOutlined, CopyOutlined, StarOutlined, StarFilled,
  ReloadOutlined, SendOutlined,
  EnvironmentOutlined, CloudOutlined, CalendarOutlined,
  ShoppingOutlined, GiftOutlined, PictureOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/auth';
import aiContentService from '../services/aiContentService';
import ProductSelector from '../components/ProductSelector';
import CampaignSelector from '../components/CampaignSelector';

const { Text } = Typography;
const { TextArea } = Input;

const CONTENT_TYPES = [
  { key: 'PRODUCT_PROMO', label: '带货种草', icon: '🛍️', desc: '产品推广+限时促销' },
  { key: 'ENGAGEMENT', label: '互动福利', icon: '🎁', desc: '互动话题/抽奖/福利' },
  { key: 'BRAND', label: '品牌故事', icon: '💡', desc: '品牌理念/故事/温度' },
  { key: 'DAILY', label: '日常问候', icon: '☀️', desc: '早安/午安/晚安问候' },
  { key: 'HOLIDAY', label: '节日祝福', icon: '🎉', desc: '节日/节气/纪念日' },
];

const MASS_SEND_ITEMS = [
  { key: 'moment', label: '朋友圈群发', path: '/moment-mass-send/create' },
  { key: 'customer', label: '客户群发', path: '/customer-mass-send/create' },
  { key: 'group', label: '社群群发', path: '/group-mass-send/create' },
];

const AiContentGenerate = ({ styleReference, defaultProvider, cities = [] }) => {
  const navigate = useNavigate();
  const { tenant } = useAuth();
  const tenantId = tenant?.id;

  const [contentType, setContentType] = useState('PRODUCT_PROMO');
  const [productIds, setProductIds] = useState([]);
  const [campaignId, setCampaignId] = useState(null);
  const [customRequirement, setCustomRequirement] = useState('');
  const [city, setCity] = useState(cities.length > 0 ? cities[0] : '上海');
  const [generateMode, setGenerateMode] = useState('merged');

  const [generating, setGenerating] = useState(false);
  const [generatedText, setGeneratedText] = useState('');
  const [currentHistoryId, setCurrentHistoryId] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);

  // Multi-product individual generation results
  const [individualResults, setIndividualResults] = useState([]);
  const [currentGenIndex, setCurrentGenIndex] = useState(-1);

  const [sceneContext, setSceneContext] = useState(null);
  const [recentHistory, setRecentHistory] = useState([]);

  const textRef = useRef(null);
  const eventSourceRef = useRef(null);

  useEffect(() => {
    if (tenantId) {
      fetchSceneContext();
      fetchRecentHistory();
    }
    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Update city when cities config changes
  useEffect(() => {
    if (cities.length > 0 && !cities.includes(city)) {
      setCity(cities[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cities]);

  // Re-fetch scene context when city changes
  useEffect(() => {
    if (tenantId && city) {
      fetchSceneContext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, city]);

  const fetchSceneContext = async () => {
    try {
      const response = await aiContentService.getSceneContext(city);
      if (response.success) setSceneContext(response.data);
    } catch (error) { console.error('获取场景信息失败:', error); }
  };

  const fetchRecentHistory = async () => {
    try {
      const response = await aiContentService.getHistory({ tenantId, page: 0, size: 6 });
      if (response.success) setRecentHistory(response.data.content || []);
    } catch (error) { console.error('获取历史失败:', error); }
  };

  const doStreamGenerate = (params, callbacks = {}) => {
    const url = aiContentService.getStreamUrl(params);
    const token = localStorage.getItem('token');

    fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-ID': tenantId,
      },
    }).then(response => {
      // Check response status before reading stream
      if (!response.ok) {
        return response.text().then(errorText => {
          console.error('SSE请求失败:', response.status, errorText);
          const errorMsg = response.status === 401 ? '认证失败，请重新登录' :
                          response.status === 403 ? '无权限访问' :
                          response.status === 500 ? '服务器错误，请稍后重试' :
                          '生成失败，请检查AI供应商配置';
          throw new Error(errorMsg);
        });
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let isErrorEvent = false;

      const read = () => {
        reader.read().then(({ done, value }) => {
          if (done) {
            if (callbacks.onDone) callbacks.onDone();
            return;
          }

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data:')) {
              const data = line.substring(5).trim();
              if (!data) continue;

              if (isErrorEvent) {
                isErrorEvent = false;
                if (callbacks.onError) callbacks.onError(data);
                return;
              }

              try {
                const parsed = JSON.parse(data);
                if (parsed.done) {
                  if (callbacks.onComplete) callbacks.onComplete(parsed);
                  continue;
                }
              } catch (e) {
                // Not JSON, it's a text token
              }

              if (callbacks.onToken) callbacks.onToken(data);
            } else if (line.startsWith('event:done') || line.startsWith('event: done')) {
              // Next data line will be the done payload
            } else if (line.startsWith('event:error') || line.startsWith('event: error')) {
              isErrorEvent = true;
            }
          }

          read();
        });
      };

      read();
    }).catch(error => {
      console.error('SSE连接失败:', error);
      if (callbacks.onError) callbacks.onError('生成失败，请检查AI供应商配置');
    });
  };

  const handleGenerate = () => {
    if (eventSourceRef.current) eventSourceRef.current.close();

    // Multi-product individual generation
    if (productIds.length > 1 && generateMode === 'individual') {
      handleIndividualGenerate();
      return;
    }

    setGenerating(true);
    setGeneratedText('');
    setCurrentHistoryId(null);
    setSelectedImages([]);
    setIsFavorite(false);
    setIndividualResults([]);

    const params = {
      tenantId,
      contentType,
      campaignId,
      customRequirement: customRequirement || null,
      styleReference: styleReference || null,
      city,
      provider: defaultProvider || null,
      imageCount: 3,
    };

    if (productIds.length > 1) {
      params.productIds = productIds.join(',');
      params.generateMode = 'merged';
    } else if (productIds.length === 1) {
      params.productId = productIds[0];
    }

    doStreamGenerate(params, {
      onToken: (data) => setGeneratedText(prev => prev + data),
      onComplete: (parsed) => {
        setCurrentHistoryId(parsed.historyId);
        setSelectedImages(parsed.images || []);
      },
      onDone: () => {
        setGenerating(false);
        fetchRecentHistory();
      },
      onError: (errMsg) => {
        setGenerating(false);
        message.error(errMsg);
      },
    });
  };

  const handleIndividualGenerate = async () => {
    setGenerating(true);
    setGeneratedText('');
    setCurrentHistoryId(null);
    setSelectedImages([]);
    setIsFavorite(false);
    setIndividualResults([]);

    for (let i = 0; i < productIds.length; i++) {
      setCurrentGenIndex(i);
      const params = {
        tenantId,
        contentType,
        productId: productIds[i],
        campaignId,
        customRequirement: customRequirement || null,
        styleReference: styleReference || null,
        city,
        provider: defaultProvider || null,
        imageCount: 3,
      };

      await new Promise((resolve) => {
        let text = '';
        doStreamGenerate(params, {
          onToken: (data) => {
            text += data;
            setIndividualResults(prev => {
              const updated = [...prev];
              updated[i] = { text, images: [], historyId: null };
              return updated;
            });
          },
          onComplete: (parsed) => {
            setIndividualResults(prev => {
              const updated = [...prev];
              updated[i] = { text, images: parsed.images || [], historyId: parsed.historyId };
              return updated;
            });
          },
          onDone: () => resolve(),
          onError: (errMsg) => {
            setIndividualResults(prev => {
              const updated = [...prev];
              updated[i] = { text: `[生成失败] ${errMsg}`, images: [], historyId: null };
              return updated;
            });
            resolve();
          },
        });
      });
    }

    setCurrentGenIndex(-1);
    setGenerating(false);
    fetchRecentHistory();
  };

  const handleCopy = (text) => {
    const content = text || generatedText;
    if (content) {
      navigator.clipboard.writeText(content).then(() => message.success('已复制到剪贴板'));
    }
  };

  const handleToggleFavorite = async () => {
    if (!currentHistoryId) return;
    try {
      const response = await aiContentService.toggleFavorite(currentHistoryId);
      if (response.success) {
        setIsFavorite(!isFavorite);
        message.success(isFavorite ? '已取消收藏' : '已收藏');
      }
    } catch (error) { message.error('操作失败'); }
  };

  const handleMassSend = (path, text, images) => {
    const content = text || generatedText;
    const imgs = images || selectedImages;
    if (!content) {
      message.warning('请先生成内容');
      return;
    }
    navigate(path, {
      state: {
        aiGeneratedText: content,
        aiGeneratedImages: imgs,
      },
    });
  };

  const massSendMenuItems = MASS_SEND_ITEMS.map(item => ({
    key: item.key,
    label: item.label,
    onClick: () => handleMassSend(item.path),
  }));

  const getMassSendMenuItems = (text, images) =>
    MASS_SEND_ITEMS.map(item => ({
      key: item.key,
      label: item.label,
      onClick: () => handleMassSend(item.path, text, images),
    }));

  return (
    <div>
      <Row gutter={16}>
        {/* 左侧：配置面板 */}
        <Col xs={24} lg={8}>
          <Card size="small" style={{ marginBottom: 16 }}>
            <div style={{ marginBottom: 12 }}>
              <Text strong>内容类型</Text>
              <div style={{ marginTop: 8 }}>
                <Radio.Group value={contentType} onChange={(e) => setContentType(e.target.value)} buttonStyle="solid" size="small">
                  {CONTENT_TYPES.map(t => (
                    <Radio.Button key={t.key} value={t.key} style={{ marginBottom: 4 }}>
                      {t.icon} {t.label}
                    </Radio.Button>
                  ))}
                </Radio.Group>
              </div>
              <div style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {CONTENT_TYPES.find(t => t.key === contentType)?.desc}
                </Text>
              </div>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            <div style={{ marginBottom: 12 }}>
              <Text strong><ShoppingOutlined /> 选择商品（可多选）</Text>
              <div style={{ marginTop: 8 }}>
                <ProductSelector
                  value={productIds}
                  onChange={(val) => setProductIds(val || [])}
                  style={{ width: '100%' }}
                  mode="multiple"
                />
              </div>
              {productIds.length > 1 && (
                <div style={{ marginTop: 8 }}>
                  <Radio.Group value={generateMode} onChange={(e) => setGenerateMode(e.target.value)} size="small">
                    <Radio.Button value="merged">合并生成</Radio.Button>
                    <Radio.Button value="individual">逐个生成</Radio.Button>
                  </Radio.Group>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {generateMode === 'merged' ? '将所有商品融合在一条文案中' : `分别为${productIds.length}个商品各生成一条文案`}
                    </Text>
                  </div>
                </div>
              )}
            </div>

            <div style={{ marginBottom: 12 }}>
              <Text strong><GiftOutlined /> 选择活动</Text>
              <div style={{ marginTop: 8 }}>
                <CampaignSelector value={campaignId} onChange={setCampaignId} style={{ width: '100%' }} />
              </div>
            </div>

            <Divider style={{ margin: '8px 0' }} />

            {/* 场景信息 */}
            <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f6f8fa', borderRadius: 6 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <Text strong style={{ fontSize: 12 }}>场景信息</Text>
                {cities.length > 0 ? (
                  <Select
                    size="small"
                    value={city}
                    onChange={(val) => setCity(val)}
                    style={{ width: 100 }}
                  >
                    {cities.map(c => (
                      <Select.Option key={c} value={c}>{c}</Select.Option>
                    ))}
                  </Select>
                ) : (
                  <Text type="secondary" style={{ fontSize: 11 }}>自动获取</Text>
                )}
              </div>
              {sceneContext && (
                <div style={{ fontSize: 12, color: '#666' }}>
                  <Space wrap size={8}>
                    <span><EnvironmentOutlined /> {sceneContext.city}</span>
                    <span><CloudOutlined /> {sceneContext.weather}</span>
                    <span><CalendarOutlined /> {sceneContext.timeOfDay} · {sceneContext.season}</span>
                    {sceneContext.holiday && <Tag color="orange" style={{ fontSize: 11 }}>{sceneContext.holiday}</Tag>}
                  </Space>
                </div>
              )}
              {!sceneContext && (
                <div style={{ fontSize: 12, color: '#999' }}>
                  <Spin size="small" /> 加载场景信息...
                </div>
              )}
            </div>

            {/* 自定义要求 */}
            <div style={{ marginBottom: 12 }}>
              <Text strong>自定义要求</Text>
              <TextArea
                rows={3}
                placeholder="如：突出新鲜感、多用emoji、文案控制在3行以内..."
                value={customRequirement}
                onChange={(e) => setCustomRequirement(e.target.value)}
                style={{ marginTop: 4 }}
                maxLength={500}
                showCount
              />
            </div>

            <Button
              type="primary"
              icon={<ThunderboltOutlined />}
              onClick={handleGenerate}
              loading={generating}
              block
              size="large"
            >
              {generating ? (currentGenIndex >= 0 ? `生成中 (${currentGenIndex + 1}/${productIds.length})...` : '生成中...') : '生成内容'}
            </Button>
          </Card>
        </Col>

        {/* 右侧：生成结果 */}
        <Col xs={24} lg={16}>
          {/* 逐个生成模式的多结果展示 */}
          {individualResults.length > 0 && (
            <Space direction="vertical" size={12} style={{ width: '100%', marginBottom: 16 }}>
              {individualResults.map((result, idx) => (
                <Card key={idx} size="small" title={`商品 ${idx + 1}`}>
                  <div style={{
                    padding: 12, background: '#fafbfc', borderRadius: 8, border: '1px solid #e8e8e8',
                    whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: 14, marginBottom: 8,
                  }}>
                    {result.text}
                    {generating && idx === individualResults.length - 1 && <span className="typing-cursor">|</span>}
                  </div>
                  {result.images?.length > 0 && (
                    <div style={{ marginBottom: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {result.images.map((img, i) => (
                        <Image key={i} src={img.ossUrl || img.url} width={80} height={80} style={{ objectFit: 'cover', borderRadius: 6 }} />
                      ))}
                    </div>
                  )}
                  {!generating && (
                    <Space size={8}>
                      <Button size="small" icon={<CopyOutlined />} onClick={() => handleCopy(result.text)}>复制</Button>
                      <Dropdown menu={{ items: getMassSendMenuItems(result.text, result.images) }} placement="bottomRight">
                        <Button size="small" icon={<SendOutlined />}>群发</Button>
                      </Dropdown>
                    </Space>
                  )}
                </Card>
              ))}
            </Space>
          )}

          {/* 单结果 / 合并结果展示 */}
          {individualResults.length === 0 && (
            <Card size="small" style={{ minHeight: 400, marginBottom: 16 }}>
              <Text strong>生成结果</Text>
              <div
                ref={textRef}
                style={{
                  marginTop: 12, minHeight: 200, padding: 16, background: '#fafbfc',
                  borderRadius: 8, border: '1px solid #e8e8e8',
                  whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: 15,
                }}
              >
                {generating && !generatedText && (
                  <div style={{ textAlign: 'center', padding: 40 }}>
                    <Spin size="large" />
                    <div style={{ marginTop: 12, color: '#999' }}>AI 正在为您创作内容...</div>
                  </div>
                )}
                {generatedText || (!generating && (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="选择参数后点击「生成内容」开始创作" />
                ))}
                {generating && generatedText && <span className="typing-cursor">|</span>}
              </div>

              {/* 配图展示 */}
              {selectedImages.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <Text strong><PictureOutlined /> 智能配图</Text>
                  <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {selectedImages.map((img, index) => (
                      <Image key={index} src={img.ossUrl || img.url} width={100} height={100}
                        style={{ objectFit: 'cover', borderRadius: 8 }} />
                    ))}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: 'block' }}>
                    已智能避开3天内使用的图片
                  </Text>
                </div>
              )}

              {/* 操作栏 */}
              {generatedText && !generating && (
                <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button icon={<CopyOutlined />} onClick={() => handleCopy()}>复制文案</Button>
                  <Button
                    icon={isFavorite ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                    onClick={handleToggleFavorite}
                  >
                    {isFavorite ? '已收藏' : '收藏'}
                  </Button>
                  <Button icon={<ReloadOutlined />} onClick={handleGenerate}>重新生成</Button>
                  <Dropdown menu={{ items: massSendMenuItems }} placement="bottomRight">
                    <Button icon={<SendOutlined />}>一键群发</Button>
                  </Dropdown>
                </div>
              )}
            </Card>
          )}

          {/* 最近生成 */}
          {recentHistory.length > 0 && (
            <Card size="small" title="最近生成">
              <Row gutter={[12, 12]}>
                {recentHistory.map(item => (
                  <Col key={item.id} xs={24} sm={12} md={8}>
                    <Card size="small" hoverable style={{ height: 120, overflow: 'hidden' }}
                      onClick={() => {
                        setGeneratedText(item.generatedText);
                        setCurrentHistoryId(item.id);
                        setIsFavorite(item.isFavorite);
                        setSelectedImages(item.images || []);
                        setIndividualResults([]);
                      }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <Tag style={{ fontSize: 10 }}>{CONTENT_TYPES.find(t => t.key === item.contentType)?.label || item.contentType}</Tag>
                        {item.isFavorite && <StarFilled style={{ color: '#faad14', fontSize: 12 }} />}
                      </div>
                      <Text ellipsis style={{ fontSize: 12, color: '#666' }}>
                        {(item.generatedText || '').substring(0, 60)}...
                      </Text>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </Col>
      </Row>

      <style>{`
        .typing-cursor {
          animation: blink 1s step-end infinite;
          color: #1890ff;
          font-weight: bold;
        }
        @keyframes blink {
          50% { opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default AiContentGenerate;
